/**
 * 알람 모달에 표출할 항목 생성
 *
 * 서버 응답(data) -> [{ key, title?, items: [{ key, label, link?, desc? }] }]
 *
 * 렌더링(AlarmModal)과 팝업 노출 여부(useUserAlarm)가 같은 함수를 쓰도록 분리했다.
 * 두 곳에 조건을 따로 두면 서로 어긋나서 "내용 없는 빈 모달"이 뜬다.
 *
 * 표출 대상
 *  1) 미승인 신규 사용자  : J001 전용, 1명 이상일 때만 표출
 *  2) 업무공유 새 댓글    : 권한 무관, 조건 충족 시에만 표출
 *
 * 표출할 항목이 없으면 빈 배열을 반환하고, 그 경우 알람 모달 자체가 열리지 않는다.
 */

import { decodeHtmlEntities } from '../../../utils/htmlEntity.js';

const LINKS = {
    userMng: '/manager/user_mng.do',
    businessBoard: '/board/business_board.do',
};

/** 목록이 비어있지 않은지 */
const hasRows = (rows) => Array.isArray(rows) && rows.length > 0;

/** 배열을 숫자로 합산 (문자 '3' 등으로 내려와도 동작한다) */
const sumCnt = (rows) => (Array.isArray(rows) ? rows : []).reduce((sum, cnt) => sum + Number(cnt ?? 0), 0);

/** 지역별 건수를 "(지역) 건수 (지역) 건수" 형태의 문자열로 만든다 */
const joinCntByLoca = (rows, locaOf, cntOf) =>
    (Array.isArray(rows) ? rows : [])
        .map((row, i) => `(${locaOf(row, i)}) ${cntOf(row, i)}`)
        .join(' ');

export function buildAlarmSections(data) {
    if (!data) return [];

    const {
        userAuth,
        userApplicationCnt,
        userApplicationGroup,
        newBusinessCmtList,
    } = data;

    const sections = [];

    /*
     * 미승인 신규 사용자 (J001 전용)
     * userApplicationCnt는 지역별 건수 목록(EMSINFO_USER.USERAMYN = 'N')으로 내려온다.
     */
    const userApplicationTotal = userAuth === 'J001' ? sumCnt(userApplicationCnt) : 0;

    if (userApplicationTotal > 0) {
        sections.push({
            key: 'userApplication',
            items: [{
                key: 'userApplication',
                label: `미승인 신규 사용자 [${userApplicationTotal}]명`,
                link: LINKS.userMng,
                desc: joinCntByLoca(userApplicationCnt, (_, i) => userApplicationGroup?.[i], (cnt) => cnt),
            }],
        });
    }

    /* 업무공유 게시판 - 내 게시글에 달린 새 댓글 (권한 무관, 조건 충족 시) */
    if (hasRows(newBusinessCmtList)) {
        const totalCnt = newBusinessCmtList.reduce((sum, row) => sum + Number(row.NEW_CMT_CNT ?? 0), 0);

        sections.push({
            key: 'businessCmt',
            title: `내 업무공유 게시글에 새 댓글이 [${totalCnt}]건 등록되었습니다.`,
            items: newBusinessCmtList.map((row) => ({
                key: `businessCmt-${row.NTT_SNO}`,
                label: `${decodeHtmlEntities(row.NTT_TIL)} [${row.NEW_CMT_CNT}]`,
                link: `${LINKS.businessBoard}/${row.NTT_SNO}`,
                desc: row.LAST_CMT_DAY ? `최근 댓글 : ${row.LAST_CMT_DAY}` : '',
            })),
        });
    }

    return sections;
}
